import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiUserPlus, FiSearch, FiMail, FiLock, FiEye, FiEyeOff, FiUser } from 'react-icons/fi';

interface User {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  [key: string]: any;
}

interface Props {}

function ManageUsers(props: Props) {
  const {} = props;
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [showInviteModal, setShowInviteModal] = useState<boolean>(false);

  // States copied from Register.tsx for invitation form
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/allusersinfo`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
          },
        });
        setUsers(response.data);
      } catch (err: any) {
        if (err.response && err.response.status === 404) {
          setUsers([]);
          setError('No users found');
        } else {
          setError('Failed to load users. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const filteredUsers = users.filter(
    user =>
      (user.name && user.name.toLowerCase().includes(search.toLowerCase())) ||
      (user.email && user.email.toLowerCase().includes(search.toLowerCase()))
  );

  // Invitation form handlers (copied/adapted from Register.tsx)
  const handleInviteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setInviteError('');
    setInviteSuccess('');
  };

  const validateInviteForm = () => {
    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
      setInviteError('Please fill in all fields');
      return false;
    }

    if (formData.password.length < 6) {
      setInviteError('Password must be at least 6 characters long');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setInviteError('Passwords do not match');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setInviteError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError('');
    setInviteSuccess('');

    if (!validateInviteForm()) {
      return;
    }

    setInviteLoading(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/register`,
        {
          username: formData.name,
          email: formData.email,
          password: formData.password,
        }
      );
      setInviteSuccess('User successfully invited!');
      // Optionally, re-fetch list of users after invite
      setTimeout(() => {
        setShowInviteModal(false);
        setFormData({
          name: '',
          email: '',
          password: '',
          confirmPassword: '',
        });
        setInviteSuccess('');
      }, 1500);
      // Re-fetch users after short delay so it's in sync
      setTimeout(() => {
        setLoading(true);
        axios.get(`${process.env.REACT_APP_API_URL}/auth/allusersinfo`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token') || ''}`
            }
        }).then((response) => setUsers(response.data))
          .catch(() => {})
          .finally(() => setLoading(false));
      }, 1600);
    } catch (err: any) {
      setInviteError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Registration failed. Please try again.'
      );
    } finally {
      setInviteLoading(false);
    }
  };

  return (
    <div className="p-6" style={{ height: '80vh', minHeight: '380px' }}>
      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed z-50 inset-0 flex items-center justify-center bg-gradient-to-br from-[#A7FFEB]/70 via-[#81D4FA]/80 to-[#E1BEE7]/60 animate-fade-in">
          <div
            className="relative w-full max-w-3xl mx-auto rounded-3xl shadow-2xl overflow-hidden transition-all duration-500"
            style={{ minHeight: '10vh', minWidth: '650px', background: 'rgba(255,255,255,0.97)' }}
          >
            {/* "Curtain" Top Bar */}
            <div className="absolute top-0 left-0 w-full h-[90px] bg-gradient-to-r from-[#1CAC70] to-[#34D399] opacity-80 rounded-b-[80%] z-10"></div>

            <button
              aria-label="Close"
              className="absolute top-5 right-7 z-20 p-2 rounded-full bg-white/80 hover:bg-[#FFEBEE] text-[#ED4C67] hover:text-[#B53471] shadow-lg transition-all"
              onClick={() => {
                setShowInviteModal(false);
                setFormData({
                  name: '',
                  email: '',
                  password: '',
                  confirmPassword: '',
                });
                setInviteError('');
                setInviteSuccess('');
              }}
              style={{ boxShadow: '0 6px 20px 0 rgba(0,0,0,0.06)' }}
            >
              <span className="text-2xl font-bold leading-none">&times;</span>
            </button>
            {/* Two columns Layout */}
            <div
              className="flex flex-col items-center justify-between px-6 py-14 pt-20 md:flex-row gap-8"
              style={{
                maxHeight: '80vh',
                background:
                  'linear-gradient(120deg, rgba(28,172,112,0.08) 0%, rgba(255,255,255,0.95) 90%)',
                borderRadius: '1.5rem',
                boxShadow:
                  '0 2px 24px rgba(52,64,84,0.10), 0 4px 60px 8px rgba(28,172,112,0.08)',
                border: '1.5px solid #FBC02D33',
                margin: '0 auto',
              }}
            >
              {/* Left side: description & emoji */}
              <div className="flex flex-col items-center md:items-start flex-1 min-w-[230px] max-w-[270px] px-2 py-2 mb-8 md:mb-0">
                <div className="text-5xl leading-none mt-[-28px] mb-2 select-none animate-bounce-slow">✨</div>
                <h2 className="text-2xl font-bold text-[#1CAC70] mb-1 drop-shadow-sm tracking-tight z-20 text-center md:text-left">
                  Invite a New User!
                </h2>
                <p className="text-[#667085] mb-4 text-base text-center md:text-left z-10 px-4 md:px-0">
                  Easily register a new team member to your platform.
                </p>
              </div>
              {/* Right side: the Register form (inline) */}
              <div className="flex-1 flex items-center justify-center w-full max-w-[440px] z-10">
                <div className="w-full">
                  <div className="bg-white rounded-2xl shadow-2xl p-6 w-full"> {/* Reduced p-8 to p-6 */}
                    <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center"> {/* Reduced text-2xl to text-xl, mb-6 to mb-4 */}
                      New User Registration
                    </h2>
                    {inviteError && (
                      <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-600 text-xs">{inviteError}</p>
                      </div>
                    )}
                    {inviteSuccess && (
                      <div className="mb-3 p-2.5 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-700 text-xs">{inviteSuccess}</p>
                      </div>
                    )}
                    <form onSubmit={handleInviteSubmit} className="space-y-4 w-full">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Name Field */}
                        <div>
                          <label htmlFor="name" className="block text-xs font-medium text-gray-700 mb-1">
                            Full Name
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                              <FiUser className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                              type="text"
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleInviteChange}
                              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1CAC70] focus:border-transparent outline-none transition text-sm"
                              placeholder="Enter full name"
                              required
                            />
                          </div>
                        </div>
                        {/* Email Field */}
                        <div>
                          <label htmlFor="email" className="block text-xs font-medium text-gray-700 mb-1">
                            Email Address
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                              <FiMail className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                              type="email"
                              id="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInviteChange}
                              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1CAC70] focus:border-transparent outline-none transition text-sm"
                              placeholder="Enter email"
                              required
                            />
                          </div>
                        </div>
                        {/* Password Field */}
                        <div>
                          <label htmlFor="password" className="block text-xs font-medium text-gray-700 mb-1">
                            Password
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                              <FiLock className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                              type={showPassword ? 'text' : 'password'}
                              id="password"
                              name="password"
                              value={formData.password}
                              onChange={handleInviteChange}
                              className="w-full pl-8 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1CAC70] focus:border-transparent outline-none transition text-sm"
                              placeholder="Create a password"
                              required
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute inset-y-0 right-0 pr-2 flex items-center"
                              tabIndex={-1}
                            >
                              {showPassword ? (
                                <FiEyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                              ) : (
                                <FiEye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                              )}
                            </button>
                          </div>
                          <p className="mt-1 text-[11px] text-gray-500">Must be at least 6 characters</p>
                        </div>
                        {/* Confirm Password Field */}
                        <div>
                          <label htmlFor="confirmPassword" className="block text-xs font-medium text-gray-700 mb-1">
                            Confirm Password
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                              <FiLock className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                              type={showConfirmPassword ? 'text' : 'password'}
                              id="confirmPassword"
                              name="confirmPassword"
                              value={formData.confirmPassword}
                              onChange={handleInviteChange}
                              className="w-full pl-8 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1CAC70] focus:border-transparent outline-none transition text-sm"
                              placeholder="Confirm password"
                              required
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className="absolute inset-y-0 right-0 pr-2 flex items-center"
                              tabIndex={-1}
                            >
                              {showConfirmPassword ? (
                                <FiEyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                              ) : (
                                <FiEye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                      {/* Submit Button */}
                      <div className="flex flex-col md:flex-row gap-3 mt-1">
                        <button
                          type="submit"
                          disabled={inviteLoading}
                          className="w-full md:w-auto bg-gradient-to-r from-[#1CAC70] to-[#FF9500] text-white py-2 px-6 rounded-lg font-semibold text-sm hover:from-[#16895a] hover:to-[#e6850e] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                        >
                          {inviteLoading ? 'Creating account...' : 'Invite User'}
                        </button>
                      </div>
                    </form>
                  </div>
                  <p className="text-center text-[#333]/70 text-xs mt-4">
                    © 2024 Stock Management. All rights reserved.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-[25px] font-semibold text-[#101828]">User Management</h1>
          <p className="text-xs text-[#667085] mt-1">
            Invite, search, and manage platform users.
          </p>
        </div>
        <button
          className="inline-flex items-center gap-2 bg-[#1CAC70] text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-[#16895a] transition"
          onClick={() => setShowInviteModal(true)}
        >
          <FiUserPlus />
          Invite User
        </button>
      </div>

      <div className="mt-6 bg-white rounded-[12px] shadow-sm border border-[#ECEFF9] h-[calc(80vh-96px)] flex flex-col">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 p-3 border-b border-[#F1F4F9]">
          <div className="relative w-full sm:w-80">
            <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search by name or email"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-2 py-1.5 border border-[#D0D5DD] rounded-lg text-xs text-[#344054] focus:outline-none focus:ring-2 focus:ring-[#1CAC70] focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setSearch('')}
            className="bg-[#F4F6F8] text-[#3F4765] px-2.5 py-1.5 rounded-lg text-xs font-semibold hover:bg-[#e9edf3] transition"
          >
            Clear
          </button>
        </div>

        <div className="p-3 flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-xs text-[#667085] flex items-center gap-2">
              <span>Loading users...</span>
            </div>
          ) : error ? (
            <div className="text-xs text-red-600 flex items-center gap-2">
              <FiMail className="text-[#1CAC70]" />
              <span>{error}</span>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-xs text-[#667085] flex items-center gap-2">
              <FiMail className="text-[#1CAC70]" />
              <span>No users found.</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse mt-1.5">
                <thead className="bg-[#FCFCFD]">
                  <tr>
                    <th className="px-3 py-2 text-[#667085] font-semibold text-xs border-b">Name</th>
                    <th className="px-3 py-2 text-[#667085] font-semibold text-xs border-b">Email</th>
                    <th className="px-3 py-2 text-[#667085] font-semibold text-xs border-b">Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 border-b text-xs text-[#101828]">{user.name}</td>
                      <td className="px-3 py-2 border-b text-xs text-[#344054]">{user.email || '-'}</td>
                      <td className="px-3 py-2 border-b text-xs text-[#344054]">{user.phone || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ManageUsers;
